/**
 * Triggered Alert Action Functions - Fetch operations for Triggered Alerts
 *
 * These functions handle all triggered alert fetch operations.
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { TriggeredAlertSchema } from "@/isomorphic/schemas";
import { getAtomStore } from "@lib/atoms";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidatedArray } from "@/frontend/lib/validation";
import {
  _apiFollowUpAlertsAtom,
  _apiWorkspaceUnresolvedAlertsAtom,
  followUpAlertsStatusAtom,
  followUpAlertsErrorAtom,
  workspaceUnresolvedAlertsStatusAtom,
  workspaceUnresolvedAlertsErrorAtom,
} from "../core/triggered-alerts";

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch all alerts for a follow-up
 */
export const fetchFollowUpAlerts = async (jwt: string, followUpId: string): Promise<void> => {
  const store = getAtomStore();

  if (!jwt || !followUpId) {
    return;
  }

  store.set(followUpAlertsStatusAtom, "loading");
  store.set(followUpAlertsErrorAtom, null);

  const result = await fetchValidatedArray(TriggeredAlertSchema, API_ROUTES.triggeredAlerts.getByFollowUp(followUpId), {
    jwt,
    context: `Follow-up ${followUpId} Alerts`,
  });

  if (result.success) {
    store.set(_apiFollowUpAlertsAtom, result.data);
    store.set(followUpAlertsStatusAtom, "success");
  } else {
    console.error("[TriggeredAlertActions] Error fetching follow-up alerts:", result.error);
    store.set(followUpAlertsStatusAtom, "error");
    store.set(followUpAlertsErrorAtom, result.error);
  }
};

/**
 * Fetch all unresolved alerts for a workspace
 */
export const fetchWorkspaceUnresolvedAlerts = async (jwt: string, workspaceId: string): Promise<void> => {
  const store = getAtomStore();

  if (!jwt || !workspaceId) {
    return;
  }

  store.set(workspaceUnresolvedAlertsStatusAtom, "loading");
  store.set(workspaceUnresolvedAlertsErrorAtom, null);

  const result = await fetchValidatedArray(
    TriggeredAlertSchema,
    API_ROUTES.triggeredAlerts.getUnresolvedByWorkspace(workspaceId),
    { jwt, context: "Workspace Unresolved Alerts" },
  );

  if (result.success) {
    store.set(_apiWorkspaceUnresolvedAlertsAtom, result.data);
    store.set(workspaceUnresolvedAlertsStatusAtom, "success");
  } else {
    console.error("[TriggeredAlertActions] Error fetching workspace unresolved alerts:", result.error);
    store.set(workspaceUnresolvedAlertsStatusAtom, "error");
    store.set(workspaceUnresolvedAlertsErrorAtom, result.error);
  }
};
